import React, { useState, useEffect } from 'react';
import { MousePointer2 } from 'lucide-react';
import { cn } from './Navbar';

function ShufflerCard() {
    const [cards, setCards] = useState(['Rapid Prototyping', 'Agile Iteration', 'Quick Time-to-Market']);

    useEffect(() => {
        const interval = setInterval(() => {
            setCards(prev => {
                const newCards = [...prev];
                const last = newCards.pop();
                newCards.unshift(last);
                return newCards;
            });
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="relative w-full h-48 flex items-center justify-center -mt-6">
            {cards.map((label, index) => {
                const isTop = index === 0;
                const scale = 1 - index * 0.1;
                const translateY = index * 16;
                const opacity = 1 - index * 0.3;
                const zIndex = 3 - index;

                return (
                    <div
                        key={label}
                        className="absolute p-4 w-[90%] md:w-4/5 rounded-2xl border border-dark/10 shadow-sm flex items-center justify-between transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
                        style={{
                            transform: `translateY(${translateY}px) scale(${scale})`,
                            opacity,
                            zIndex,
                            backgroundColor: isTop ? '#8A3590' : '#F2F0E9', // Accent to match Preset A
                            color: isTop ? '#F2F0E9' : '#1A1A1A'
                        }}
                    >
                        <span className="font-heading text-sm font-bold">{label}</span>
                        <div className={cn("w-2 h-2 rounded-full", isTop ? "bg-background animate-pulse" : "bg-dark/20")} />
                    </div>
                );
            })}
        </div>
    );
}

function TypewriterCard() {
    const [text, setText] = useState('');
    const [msgIndex, setMsgIndex] = useState(0);
    const messages = [
        "> Compiling iOS target...",
        "> Building Android ARM64...",
        "> Optimizing Web bundle...",
        "> Zero-cost native bridging...",
        "> Verified: 120fps standard."
    ];

    useEffect(() => {
        let currentMsg = messages[msgIndex];
        let charIndex = 0;
        let timerId;

        const typeChar = () => {
            setText(currentMsg.substring(0, charIndex + 1));
            charIndex++;
            if (charIndex < currentMsg.length) {
                timerId = setTimeout(typeChar, 40 + Math.random() * 60);
            } else {
                timerId = setTimeout(() => {
                    setMsgIndex((prev) => (prev + 1) % messages.length);
                }, 1500);
            }
        };

        timerId = setTimeout(typeChar, 500);
        return () => clearTimeout(timerId);
    }, [msgIndex]);

    return (
        <div className="w-full h-48 bg-dark rounded-2xl border border-dark/20 p-5 mt-4 flex flex-col justify-between overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-accent/20">
                <div className="h-full bg-accent animate-[pulse_2s_infinite] w-1/3"></div>
            </div>
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 rounded-full bg-accent animate-pulse"></div>
                    <span className="text-xs font-data text-accent uppercase tracking-widest">Live Feed</span>
                </div>
                <div className="font-data text-xs text-[#00FF41] opacity-60 mb-2">Flutter engine v3.24 running...</div>
                <div className="font-data text-xs md:text-sm text-background break-all">
                    {text}<span className="inline-block w-2 h-4 bg-accent ml-1 -mb-1 animate-pulse"></span>
                </div>
            </div>
        </div>
    );
}

function SchedulerCard() {
    const [activeDay, setActiveDay] = useState(-1);
    const [cursorPos, setCursorPos] = useState({ x: 10, y: 150 });
    const [pressed, setPressed] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        const cycle = async () => {
            setActiveDay(-1);
            setSaved(false);

            const targetDay = Math.floor(Math.random() * 5) + 1; // 1-5 (Mon-Fri)
            setCursorPos({ x: 20 + targetDay * 36, y: 40 });
            await new Promise(r => setTimeout(r, 800));

            setPressed(true);
            await new Promise(r => setTimeout(r, 200));

            setActiveDay(targetDay);
            setPressed(false);
            await new Promise(r => setTimeout(r, 600));

            setCursorPos({ x: 180, y: 130 });
            await new Promise(r => setTimeout(r, 800));

            setPressed(true);
            await new Promise(r => setTimeout(r, 200));
            setSaved(true);
            setPressed(false);

            await new Promise(r => setTimeout(r, 300));
            setCursorPos({ x: 280, y: 180 });

            await new Promise(r => setTimeout(r, 1000));
            cycle();
        };

        cycle();
    }, []);

    const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    return (
        <div className="w-full h-48 bg-background rounded-2xl border border-dark/10 p-5 mt-4 relative overflow-hidden">
            <div className="grid grid-cols-7 gap-1 md:gap-2 mb-8">
                {days.map((d, i) => (
                    <div
                        key={i}
                        className={cn(
                            "h-8 md:h-10 flex items-center justify-center rounded-md font-heading text-xs font-bold transition-all duration-300",
                            activeDay === i
                                ? "bg-accent text-background scale-105"
                                : "bg-primary/5 text-dark/40"
                        )}
                    >
                        {d}
                    </div>
                ))}
            </div>

            <div className="flex justify-end pr-2">
                <button className={cn(
                    "px-6 py-2 rounded-full text-xs font-bold transition-colors duration-300",
                    saved ? "bg-primary text-background" : "bg-dark/10 text-dark"
                )}>
                    {saved ? "Confirmed" : "Lock Price"}
                </button>
            </div>

            <MousePointer2
                className="absolute z-10 transition-all duration-700 ease-in-out"
                style={{
                    transform: `translate(${cursorPos.x}px, ${cursorPos.y}px) scale(${pressed ? 0.8 : 1})`,
                    fill: '#1A1A1A', stroke: '#1A1A1A'
                }}
                size={24}
            />
        </div>
    );
}

export default function Features() {
    return (
        <section className="py-24 px-6 md:px-16 bg-background relative" id="expertise">
            <div className="max-w-6xl mx-auto">
                <div className="mb-16 max-w-2xl">
                    <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4 tracking-tight">Functional <span className="text-drama text-accent">Artifacts</span></h2>
                    <p className="text-dark/70 font-subheading text-lg">
                        We operate differently. Every project gets a dedicated squad executing a highly opinionated workflow optimized for speed, cross-platform performance, and transparency.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="bg-white rounded-[2rem] p-8 border border-dark/5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
                        <h3 className="font-heading font-bold text-2xl mb-2">Velocity</h3>
                        <p className="font-subheading text-dark/70 text-sm mb-6 max-w-[200px]">
                            Fast development cycles using established, reusable modules.
                        </p>
                        <ShufflerCard />
                    </div>

                    <div className="bg-white rounded-[2rem] p-8 border border-dark/5 shadow-sm hover:shadow-md transition-shadow">
                        <h3 className="font-heading font-bold text-2xl mb-2">Native Grade</h3>
                        <p className="font-subheading text-dark/70 text-sm mb-4 max-w-[250px]">
                            Multi-platform development with absolute native performance parity.
                        </p>
                        <TypewriterCard />
                    </div>

                    <div className="bg-white rounded-[2rem] p-8 border border-dark/5 shadow-sm hover:shadow-md transition-shadow">
                        <h3 className="font-heading font-bold text-2xl mb-2">Transparent Cost</h3>
                        <p className="font-subheading text-dark/70 text-sm mb-4 max-w-[220px]">
                            Competitive, predictable pricing scheduled precisely to your sprints.
                        </p>
                        <SchedulerCard />
                    </div>
                </div>
            </div>
        </section>
    );
}
